import { useOktaAuth } from '@okta/okta-react';
import { ComponentWithoutHOCProps } from 'common/types/component';
import { Loading } from 'common/utils/loading';
import { ReactElement, useEffect, useState } from 'react';
import { withAuthLayout } from '../login/authLayout.page';
import { CILogger } from 'common/log/log.provider';
import { OktaErrorDisplay } from './OktaErrorDisplay';
import { useLocation } from 'react-router';

// Inspired by https://github.com/okta/okta-react/blob/master/src/LoginCallback.tsx
let handledRedirect = false;

const _OktaLoginCallback = (): ReactElement => {
    const { oktaAuth, authState } = useOktaAuth();
    const location = useLocation();
    const [callbackError, setCallbackError] = useState(null);

    useEffect(() => {
        // OKTA-635977: Prevents multiple calls of handleLoginRedirect() in React18 StrictMode
        // Top-level variable solution follows: https://react.dev/learn/you-might-not-need-an-effect#initializing-the-application
        if (!handledRedirect) {
            const searchParams = new URLSearchParams(location.search);
            const hasState = searchParams.has('state');

            if (!hasState) {
                // Not valid okta callback -> redirect to homepage
                window.location.href = '/';

                return;
            }

            oktaAuth.handleRedirect('/sso/okta/postLogin').catch(async (e) => {
                if (oktaAuth?.isAuthenticated()) {
                    CILogger.getInstance().info(
                        'Error flow - user visited okta postLogin page when already authenticated',
                        e
                    );
                }

                setCallbackError(e);
            });
            handledRedirect = true;
        }
    }, [oktaAuth, location.search]);

    const authError = authState?.error;
    const displayError = callbackError || authError;

    const handleGoLoginPage = async () => {
        await oktaAuth?.signOut();
        window.location.href = '/';
    };

    if (displayError) {
        return <OktaErrorDisplay error={displayError} onGoToLogin={handleGoLoginPage} />;
    }

    return <Loading isLoading={true} />;
};

const OktaLoginCallback: ComponentWithoutHOCProps<null> = withAuthLayout(_OktaLoginCallback);

export default OktaLoginCallback;
